import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { baseUrl } from "../index";
import { getAuthToken } from "../../utils/utils";

export const submitTrialRequest = createAsyncThunk(
  "trial/submit",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${baseUrl}/trial-request`, formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to submit trial request");
    }
  }
);

export const fetchTrialRequests = createAsyncThunk(
  "trial/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${baseUrl}/admin/trial-requests`, {
        headers: { Authorization: token },
      });
      return res.data.requests;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch trial requests");
    }
  }
);

export const updateTrialRequest = createAsyncThunk(
  "trial/update",
  async ({ id, status, notes }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.put(
        `${baseUrl}/admin/trial-requests/${id}`,
        { status, notes },
        { headers: { Authorization: token } }
      );
      return res.data.request;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to update trial request");
    }
  }
);

export const sendAdminEmail = createAsyncThunk(
  "trial/sendEmail",
  async ({ subject, message, userIds }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${baseUrl}/admin/send-email`,
        { subject, message, userIds },
        { headers: { Authorization: token } }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to send emails");
    }
  }
);

export const fetchEmailDrafts = createAsyncThunk(
  "trial/fetchEmailDrafts",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${baseUrl}/admin/email-drafts`, {
        headers: { Authorization: token },
      });
      return res.data.drafts;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch drafts");
    }
  }
);

export const saveEmailDraft = createAsyncThunk(
  "trial/saveEmailDraft",
  async ({ subject, message, userIds, targetMode }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${baseUrl}/admin/email-drafts`,
        { subject, message, userIds, targetMode },
        { headers: { Authorization: token } }
      );
      return res.data.draft;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to save draft");
    }
  }
);

export const deleteEmailDraft = createAsyncThunk(
  "trial/deleteEmailDraft",
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${baseUrl}/admin/email-drafts/${id}`, {
        headers: { Authorization: token },
      });
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to delete draft");
    }
  }
);

export const sendEmailDraft = createAsyncThunk(
  "trial/sendEmailDraft",
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${baseUrl}/admin/email-drafts/${id}/send`,
        {},
        { headers: { Authorization: token } }
      );
      return { id, message: res.data.message };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to send draft");
    }
  }
);

export const fetchAnnouncements = createAsyncThunk(
  "trial/fetchAnnouncements",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${baseUrl}/admin/announcements`, {
        headers: { Authorization: token },
      });
      return res.data.announcements;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch announcements");
    }
  }
);

export const fetchPublicAnnouncements = createAsyncThunk(
  "trial/fetchPublicAnnouncements",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${baseUrl}/api/announcements`);
      return res.data.announcements;
    } catch (err) {
      return rejectWithValue("Failed to fetch announcements");
    }
  }
);

export const createAnnouncement = createAsyncThunk(
  "trial/createAnnouncement",
  async (data, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.post(`${baseUrl}/admin/announcements`, data, {
        headers: { Authorization: token },
      });
      return res.data.announcement;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to create announcement");
    }
  }
);

export const updateAnnouncement = createAsyncThunk(
  "trial/updateAnnouncement",
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.put(`${baseUrl}/admin/announcements/${id}`, data, {
        headers: { Authorization: token },
      });
      return res.data.announcement;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to update announcement");
    }
  }
);

export const resendTrialCredentials = createAsyncThunk(
  "trial/resendCredentials",
  async (email, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${baseUrl}/resend-trial-credentials`, { email });
      return res.data.message;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to resend credentials");
    }
  }
);

export const adminResendCredentials = createAsyncThunk(
  "trial/adminResendCredentials",
  async (userId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${baseUrl}/admin/resend-trial-credentials/${userId}`,
        {},
        { headers: { Authorization: token } }
      );
      return res.data.message;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to resend credentials");
    }
  }
);

export const deleteAnnouncement = createAsyncThunk(
  "trial/deleteAnnouncement",
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${baseUrl}/admin/announcements/${id}`, {
        headers: { Authorization: token },
      });
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to delete announcement");
    }
  }
);

const trialSlice = createSlice({
  name: "trial",
  initialState: {
    requests: [],
    announcements: [],
    drafts: [],
    loading: false,
    actionLoading: false,
    emailLoading: false,
    draftLoading: false,
    submitted: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearTrialMessages(state) {
      state.error = null;
      state.successMessage = null;
      state.submitted = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitTrialRequest.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(submitTrialRequest.fulfilled, (state) => { state.loading = false; state.submitted = true; })
      .addCase(submitTrialRequest.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchTrialRequests.pending, (state) => { state.loading = true; })
      .addCase(fetchTrialRequests.fulfilled, (state, action) => { state.loading = false; state.requests = action.payload ?? []; })
      .addCase(fetchTrialRequests.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(updateTrialRequest.fulfilled, (state, action) => {
        state.requests = state.requests.map((r) => r._id === action.payload._id ? action.payload : r);
      })

      .addCase(sendAdminEmail.pending, (state) => { state.emailLoading = true; state.error = null; state.successMessage = null; })
      .addCase(sendAdminEmail.fulfilled, (state, action) => { state.emailLoading = false; state.successMessage = action.payload.message; })
      .addCase(sendAdminEmail.rejected, (state, action) => { state.emailLoading = false; state.error = action.payload; })

      .addCase(fetchAnnouncements.pending, (state) => { state.loading = true; })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => { state.loading = false; state.announcements = action.payload ?? []; })
      .addCase(fetchAnnouncements.rejected, (state) => { state.loading = false; })

      .addCase(fetchPublicAnnouncements.fulfilled, (state, action) => { state.announcements = action.payload ?? []; })

      .addCase(createAnnouncement.fulfilled, (state, action) => { state.announcements.unshift(action.payload); state.successMessage = "Announcement created"; })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        state.announcements = state.announcements.map((a) => a._id === action.payload._id ? action.payload : a);
        state.successMessage = "Announcement updated";
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.announcements = state.announcements.filter((a) => a._id !== action.payload);
        state.successMessage = "Announcement deleted";
      })

      .addCase(fetchEmailDrafts.pending, (state) => { state.draftLoading = true; })
      .addCase(fetchEmailDrafts.fulfilled, (state, action) => { state.draftLoading = false; state.drafts = action.payload ?? []; })
      .addCase(fetchEmailDrafts.rejected, (state) => { state.draftLoading = false; })

      .addCase(saveEmailDraft.pending, (state) => { state.draftLoading = true; state.error = null; state.successMessage = null; })
      .addCase(saveEmailDraft.fulfilled, (state, action) => {
        state.draftLoading = false;
        state.drafts.unshift(action.payload);
        state.successMessage = "Draft saved successfully";
      })
      .addCase(saveEmailDraft.rejected, (state, action) => { state.draftLoading = false; state.error = action.payload; })

      .addCase(deleteEmailDraft.fulfilled, (state, action) => {
        state.drafts = state.drafts.filter((d) => d._id !== action.payload);
        state.successMessage = "Draft deleted";
      })

      .addCase(sendEmailDraft.pending, (state) => { state.emailLoading = true; state.error = null; state.successMessage = null; })
      .addCase(sendEmailDraft.fulfilled, (state, action) => {
        state.emailLoading = false;
        state.drafts = state.drafts.map((d) =>
          d._id === action.payload.id ? { ...d, status: "sent" } : d
        );
        state.successMessage = action.payload.message;
      })
      .addCase(sendEmailDraft.rejected, (state, action) => { state.emailLoading = false; state.error = action.payload; });
  },
});

export const { clearTrialMessages } = trialSlice.actions;
export default trialSlice.reducer;
